import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { formConstant } from '../../../../../config';
import { onConfirmDialog } from '../../../../core/helpers/helpers.function';
import { PageParams } from '../../../../core/models/page-params.model';
import { NotificationService } from '../../../../core/services/notification.service';
import { UploadFileService } from '../../../../core/services/upload-file.service';
import { UserService } from '../../../../core/services/user.service';
import { ConfirmPopupDialog } from '../../../../shared/popups/confirm-popup/confirm-popup.component';
import { ShipmentPriceService } from '../../../shipment-price/services/shipment-price.service';
import { CarrierCoverageMapService, ICoverageMapItem } from '../../services/carrier-coverage-map.service';
import { CoverageMapPopupComponent } from './coverage-map-popup/coverage-map-popup.component';

@Component({
    selector: 'eci-coverage-map',
    templateUrl: './coverage-map.component.html',
    styleUrls: ['./coverage-map.component.scss']
})
export class CoverageMapComponent implements OnInit, OnDestroy {

    @ViewChild('fileInput') fileInput: ElementRef;

    public carrierId: number;
    public carrierTitle = '';
    public form: FormGroup;
    public pageParams: PageParams = new PageParams();
    public dataSource = new MatTableDataSource<ICoverageMapItem>([]);
    public displayedColumns: string[] = ['postcode', 'densityZone', 'edit', 'delete'];
    public showLoader = false;
    private sub$ = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private coverageMapService: CarrierCoverageMapService,
        private shipmentPriceSvc: ShipmentPriceService,
        private notify: NotificationService,
        public uploadFileSvc: UploadFileService,
        public userSvc: UserService
    ) {
    }

    ngOnInit(): void {
        this.carrierId = Number(this.route.snapshot.paramMap.get('carrierId'));
        this.pageParams.pageSize = formConstant.pageSize;
        this.pageParams.pageIndex = 0;
        this.form = new FormGroup({
            search: new FormControl(''),
        });
        this.loadCarrierLabel();
        this.getList();
        this.sub$.add(
            this.form.get('search').valueChanges.pipe(debounceTime(500)).subscribe(() => this.getList())
        );
    }

    ngOnDestroy(): void {
        this.sub$.unsubscribe();
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    private loadCarrierLabel(): void {
        this.shipmentPriceSvc.getCarriersById(this.carrierId).subscribe((data) => {
            if (data?.name) {
                this.carrierTitle = data.name;
            }
        });
    }

    public backToList(): void {
        this.router.navigate(['/carriers/carriers-list']);
    }

    public getList(
        page: number = this.pageParams?.pageIndex + 1 || 1,
        limit: number = this.pageParams?.pageSize || 100,
        search: string = this.form.get('search')?.value || ''
    ): void {
        this.coverageMapService.getList(this.carrierId, page, limit, search).subscribe((data) => {
            if (!data) {
                return;
            }
            this.dataSource = new MatTableDataSource<ICoverageMapItem>(data.items);
            this.pageParams.pageSize = data.meta.itemsPerPage;
            this.pageParams.pageIndex = data.meta.currentPage - 1;
            this.pageParams.length = data.meta.totalItems;
        });
    }

    public pageChanged(e): void {
        this.getList(e.pageIndex + 1, e.pageSize);
    }

    public openDialog(isUpdate: boolean = false, row?: ICoverageMapItem): void {
        const dialogRef = this.dialog.open(CoverageMapPopupComponent, {
            width: '500px',
        });
        dialogRef.componentInstance.carrierId = this.carrierId;
        dialogRef.componentInstance.isUpdate = isUpdate;
        if (isUpdate) {
            dialogRef.componentInstance.info = row;
        }

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.getList(
                    this.pageParams.pageIndex + 1,
                    this.pageParams.pageSize,
                    this.form.get('search')?.value || ''
                );
            }
        });
    }

    public onDelete(id: number): void {
        const dialogRef = this.dialog.open(ConfirmPopupDialog);
        onConfirmDialog(dialogRef, () => {
            this.showLoader = true;
            this.coverageMapService.delete(id).subscribe(() => {
                this.notify.showSuccessMessage('Deleted successfully');
                this.showLoader = false;
                this.getList();
            }, (err) => {
                this.notify.showError(err?.error?.message || 'Delete failed');
                this.showLoader = false;
            });
        }, 'this entry');
    }

    public uploadExcel(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = input?.files;
        if (!files?.length) {
            return;
        }
        const types = this.uploadFileSvc.excelFileFormats[0].split(', ').map(t => t.trim());
        if (!types.includes(files[0].type)) {
            this.notify.showError('Should contain only XLSX files');
            input.value = '';
            return;
        }
        this.showLoader = true;
        this.uploadFileSvc.uploadFile(files, 'carrier-postal-codes-coverage-map/import', this.carrierId);
        this.sub$.add(
            this.uploadFileSvc.fileLoaded().subscribe(() => {
                this.notify.showSuccessMessage('Import completed successfully');
                this.showLoader = false;
                input.value = '';
                this.getList();
            }, () => {
                this.notify.showError('Import failed');
                this.showLoader = false;
                input.value = '';
            })
        );
    }
}
